import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { useTranslation } from "react-i18next";

interface user {
    _id: string;
    full_name: string;
    email_address: string;
}

interface quiz {
    _id: string;
    type: string;
    question: string;
    correct_answer: string;
    term: string;
}

interface quizData {
    _id: string;
    quiz: quiz;
}

interface History {
    _id: string;
    userId: string;
    deckId: string;
    quizId: string;
    questionId: string;
    date_studied: string;
    createdAt: string;
    is_correct: boolean;
    review_score: number;
    correct_answer: string;
    user_answer: string;
    user: user[];
    quizData: quizData;
}

interface HistoryTableProps {
    histories: History[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export default function HistoryTable({ histories, currentPage, totalPages, onPageChange }: HistoryTableProps) {
    const { t } = useTranslation();

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <div className="min-w-[1102px]">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Type of Question")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Question")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Correct Answer")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("User Answered")}
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {histories.map((history) => (
                                <TableRow
                                    key={history._id}
                                    className={history.is_correct ? "bg-green-100 dark:bg-green-900" : "bg-red-100 dark:bg-red-900"}
                                >
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {history?.quizData?.quiz?.type == "yes_no" ? "Yes/No" : history?.quizData?.quiz?.type == "multiple_choice" ? "Multiple Choice" : "Essay"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {history?.quizData?.quiz?.type == 'yes_no' ? (
                                            <>
                                                {history?.quizData?.quiz?.question} | <strong>{history?.quizData?.quiz?.term}</strong>
                                            </>
                                        ) : (
                                            history?.quizData?.quiz?.question
                                        )}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {history?.quizData?.quiz?.correct_answer}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {`${history?.user_answer}`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.05]">
                <div className="flex flex-1 justify-center">
                    <nav className="relative z-0 inline-flex -space-x-px rounded-md" aria-label="Pagination">
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => onPageChange(page)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                    ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                    }`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
}